import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { updateNotification as updateNotificationRequest } from "../api/notification.api";
import type { UpdateNotificationRequest } from "../types/notification.types";
import { useResultHandler } from "../../../hooks/useResultHandler";

interface UseUpdateNotificationReturn {
  loading: boolean;
  error: string | null;
  success: boolean;
  updateNotification: (id: string, data: UpdateNotificationRequest) => Promise<void>;
  reset: () => void;
}

export const useUpdateNotification = (): UseUpdateNotificationReturn => {
  const { t } = useTranslation();
  const { handleResult } = useResultHandler();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const updateNotification = useCallback(
    async (id: string, data: UpdateNotificationRequest) => {
      try {
        setLoading(true);
        setError(null);
        setSuccess(false);

        const response = await updateNotificationRequest(id, data);
        const isSuccess = handleResult(response, {
          successMessage: t("notification.notification_updated_successfully"),
          showErrorToast: false,
        });
        if (isSuccess) {
          setSuccess(true);
          return;
        }

        setError(response.user_message || response.message || "Failed to update notification");
      } catch (caughtError: unknown) {
        const message =
          caughtError instanceof Error
            ? caughtError.message
            : "An error occurred while updating notification";
        setError(message);
      } finally {
        setLoading(false);
      }
    },
    [handleResult, t],
  );

  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
    setSuccess(false);
  }, []);

  return {
    loading,
    error,
    success,
    updateNotification,
    reset,
  };
};
